import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { authService, type AuthUser } from '../services/auth.service';

interface AuthContextValue {
  user: AuthUser | null;
  /** `true` khi chưa biết đã đăng nhập hay chưa — đừng điều hướng trong lúc này. */
  loading: boolean;
  /** Ném lỗi nếu đăng nhập thất bại — không trả `null`. */
  login: (username: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Nguồn sự thật là SERVER, không phải localStorage.
   *
   * Bản cũ đọc user từ localStorage nên: phiên hết hạn hoặc bị admin khoá mà tab
   * còn mở thì FE vẫn tưởng đang đăng nhập và render cả trang trước khi request
   * đầu tiên trả 401. Cookie httpOnly cũng không đọc được từ JS, nên chỉ có cách
   * hỏi server.
   */
  useEffect(() => {
    let cancelled = false;

    authService
      .me()
      .then((me) => {
        if (!cancelled) setUser(me);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const { user: loggedIn } = await authService.login(username, password);
    setUser(loggedIn);
    return loggedIn;
  }, []);

  const logout = useCallback(async () => {
    // Xoá state trước: kể cả server lỗi, người dùng vẫn thoát được khỏi UI.
    setUser(null);
    await authService.logout().catch(() => undefined);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth phải nằm trong AuthProvider');
  return ctx;
}
